import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Avatar, Chip,
  IconButton, TextField, InputAdornment, CircularProgress,
  Alert, FormControl, InputLabel, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Divider, Paper, Tooltip, Badge, Snackbar
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Phone as PhoneIcon,
  AdminPanelSettings as AdminIcon,
  Gavel as LawyerIcon,
  People as PartnerIcon,
  PersonOutline as ClientIcon
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useOffice } from '../../store/officeContext';
import InviteMemberModal from '../../components/Modal/InviteMemberModal';

const ROLE_CONFIG = {
  admin: {
    label: 'Administrador',
    color: '#d32f2f',
    icon: AdminIcon,
    description: 'Acceso completo al despacho'
  },
  lawyer: {
    label: 'Abogado',
    color: '#1976d2',
    icon: LawyerIcon,
    description: 'Gestiona expedientes asignados'
  },
  partner: {
    label: 'Colaborador',
    color: '#ed6c02',
    icon: PartnerIcon,
    description: 'Apoya en expedientes específicos'
  },
  client: {
    label: 'Cliente',
    color: '#0288d1',
    icon: ClientIcon,
    description: 'Acceso a sus expedientes'
  }
};

const OfficeMembersView = () => {
  const office = useOffice().office;
  const [members, setMembers] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [savingChanges, setSavingChanges] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Estados del formulario de edición
  const [editForm, setEditForm] = useState({
    first_names: '',
    last_names: '',
    email: '',
    document_number: '',
    phone_number: '',
    role: ''
  });

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setCurrentUserId(user.id);
    }
  };

  const syncMemberPermissions = async () => {
    try {
      setSyncing(true);

      supabase.functions.invoke('sync-member-permissions', {
        body: {
          office_id: office.id
        }
      });

      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error syncing permissions:', error);
    } finally {
      setSyncing(false);
    }
  };

  const fetchMembers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('office_member')
      .select(`
        id, 
        role, 
        created_at, 
        person:member_id (
          id, 
          full_name, 
          email, 
          document_number, 
          phone_number
        )
      `)
      .eq('office_id', office.id)
      .order('created_at', { ascending: false });

    if (!error) {
      setMembers(data);
    } else {
      console.error('Error fetching members:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMembers();
    getCurrentUser();
  }, []);

  const handleEditClick = (member) => {
    const [firstNames = '', lastNames = ''] =
      member.person?.full_name?.split(';') || [];

    setEditingMember(member);
    setEditForm({
      first_names: firstNames.trim(),
      last_names: lastNames.trim(),
      email: member.person?.email || '',
      document_number: member.person?.document_number || '',
      phone_number: member.person?.phone_number || '',
      role: member.role
    });
    setEditDialogOpen(true);
  };

  const handleSaveChanges = async () => {
    if (!editingMember) return;

    if (!editForm.first_names.trim() || !editForm.last_names.trim()) {
      alert('Por favor ingrese nombres y apellidos');
      return;
    }

    const fullName = `${editForm.first_names.trim()}; ${editForm.last_names.trim()}`;

    const previousRole = editingMember.role;

    setSavingChanges(true);
    try {
      await supabase
        .from('person')
        .update({
          full_name: fullName,
          email: editForm.email,
          document_number: editForm.document_number,
          phone_number: editForm.phone_number
        })
        .eq('id', editingMember.person.id);

      await supabase
        .from('office_member')
        .update({ role: editForm.role })
        .eq('id', editingMember.id);

      if (previousRole !== 'admin' && editForm.role === 'admin') {
        await syncMemberPermissions();
      }

      await fetchMembers();
      setEditDialogOpen(false);
      setEditingMember(null);
    } catch (error) {
      console.error(error);
      alert('Error al guardar los cambios');
    } finally {
      setSavingChanges(false);
    }
  };

  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!memberToDelete) return;

    try {
      const { error } = await supabase
        .from('office_member')
        .delete()
        .eq('id', memberToDelete.id);

      if (error) throw error;

      await fetchMembers();
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
    } catch (error) {
      console.error('Error deleting member:', error);
      alert('Error al eliminar el miembro');
    }
  };

  const isCurrentUser = (member) => {
    return member.person?.id === currentUserId;
  };

  const filteredMembers = members.filter(member => {
    const matchesSearch = 
      member.person?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.person?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.person?.document_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || member.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // Agrupar miembros por rol para mejor organización
  const membersByRole = Object.keys(ROLE_CONFIG).reduce((acc, role) => {
    acc[role] = filteredMembers.filter(m => m.role === role);
    return acc;
  }, {});

  const totalCount = filteredMembers.length;

  return (
    <Box sx={{ p: { xs: 2, md: 3, mb: 100 }, maxWidth: 1400, margin: '0 auto' }}>
      {/* Header mejorado */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: `linear-gradient(135deg, ${office.color}15 0%, ${office.color}05 100%)`,
          borderLeft: `4px solid ${office.color}`
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: { xs: 'wrap', md: 'nowrap' }
          }}
        >
          {/* Título */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              Equipo del Despacho
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalCount} {totalCount === 1 ? 'miembro' : 'miembros'} • Gestiona accesos y permisos
            </Typography>
          </Box>

          {/* Acciones */}
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              alignItems: 'center',
              justifyContent: 'flex-end',
              width: { xs: '100%', md: 'auto' }
            }}
          >
            <Button
              variant="outlined"
              onClick={syncMemberPermissions}
              disabled={syncing}
              sx={{
                whiteSpace: 'nowrap',
                borderColor: office.color,
                color: office.color,
                '&:hover': {
                  borderColor: office.color,
                  bgcolor: `${office.color}10`
                }
              }}
            >
              {syncing ? 'Sincronizando...' : 'Sincronizar permisos'}
            </Button>

            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setShowInviteModal(true)}
              sx={{
                bgcolor: office.color,
                '&:hover': { bgcolor: office.color, opacity: 0.9 },
                whiteSpace: 'nowrap'
              }}
            >
              Añadir miembro
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Barra de búsqueda y filtros mejorada */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item size={{xs:12, md:6}}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre, email o DNI..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item size={{xs:12, md:3}}>
            <FormControl fullWidth>
              <InputLabel>Filtrar por rol</InputLabel>
              <Select
                value={roleFilter}
                label="Filtrar por rol"
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <MenuItem value="all">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Todos los roles
                  </Box>
                </MenuItem>
                {Object.entries(ROLE_CONFIG).map(([key, config]) => {
                  const Icon = config.icon;
                  const count = membersByRole[key]?.length || 0;
                  return (
                    <MenuItem key={key} value={key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Icon sx={{ fontSize: 18, color: config.color }} />
                        {config.label}
                        <Chip label={count} size="small" sx={{ ml: 'auto' }} />
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item size={{xs:12, md:3}}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {Object.entries(ROLE_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                const count = membersByRole[key]?.length || 0;
                return (
                  <Tooltip key={key} title={`${count} ${config.label}${count !== 1 ? 's' : ''}`}>
                    <Chip
                      icon={<Icon />}
                      label={count}
                      size="small"
                      sx={{ 
                        bgcolor: `${config.color}20`,
                        color: config.color,
                        fontWeight: 600
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Lista de miembros mejorada */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={48} />
        </Box>
      ) : filteredMembers.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50' }}>
          <PersonAddIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm || roleFilter !== 'all' 
              ? 'No se encontraron miembros'
              : 'Aún no hay miembros en el equipo'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm || roleFilter !== 'all'
              ? 'Intenta ajustar los filtros de búsqueda'
              : 'Comienza añadiendo miembros a tu despacho'}
          </Typography>
          {!searchTerm && roleFilter === 'all' && (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setShowInviteModal(true)}
              sx={{ bgcolor: office.color }}
            >
              Añadir Primer Miembro
            </Button>
          )}
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filteredMembers.map((member) => {
            const roleConfig = ROLE_CONFIG[member.role];
            const RoleIcon = roleConfig.icon;
            
            return (
              <Card 
                key={member.id}
                elevation={isCurrentUser(member) ? 3 : 1}
                sx={{ 
                  transition: 'all 0.2s',
                  border: isCurrentUser(member) ? `2px solid ${office.color}` : '1px solid',
                  borderColor: isCurrentUser(member) ? office.color : 'divider',
                  '&:hover': { 
                    boxShadow: 3,
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Grid container spacing={2} alignItems="center">
                    {/* Avatar y nombre */}
                    <Grid item size={{xs:12, sm:5, md:4}}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            <Avatar 
                              sx={{ 
                                width: 24, 
                                height: 24, 
                                bgcolor: roleConfig.color,
                                border: '2px solid white'
                              }}
                            >
                              <RoleIcon sx={{ fontSize: 14 }} />
                            </Avatar>
                          }
                        >
                          <Avatar 
                            sx={{ 
                              width: 56, 
                              height: 56,
                              bgcolor: office.color,
                              fontSize: '1.5rem',
                              fontWeight: 600
                            }}
                          >
                            {member.person?.full_name?.charAt(0)?.toUpperCase()}
                          </Avatar>
                        </Badge>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 600,
                                fontSize: '1.1rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {member.person?.full_name?.split(';').map(p => p.trim()).join(' ')}
                            </Typography>
                            {isCurrentUser(member) && (
                              <Chip 
                                label="Tú" 
                                size="small" 
                                sx={{ 
                                  bgcolor: office.color,
                                  color: 'white',
                                  fontWeight: 600,
                                  height: 20
                                }}
                              />
                            )}
                          </Box>
                          <Chip
                            icon={<RoleIcon />}
                            label={roleConfig.label}
                            size="small"
                            sx={{
                              bgcolor: `${roleConfig.color}15`,
                              color: roleConfig.color,
                              fontWeight: 600
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>

                    {/* Información de contacto */}
                    <Grid item size={{xs:12, sm:7, md:5}}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {member.person?.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {member.person.email}
                            </Typography>
                          </Box>
                        )}
                        {member.person?.document_number && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BadgeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              DNI: {member.person.document_number}
                            </Typography>
                          </Box>
                        )}
                        {member.person?.phone_number && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {member.person.phone_number}
                            </Typography>
                          </Box>
                        )}
                        {!member.person?.email && !member.person?.document_number && !member.person?.phone_number && (
                          <Typography variant="body2" color="text.disabled" fontStyle="italic">
                            Sin información de contacto
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Acciones */}
                    <Grid item size={{xs:12, md:3}}>
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        justifyContent: { xs: 'flex-start', md: 'flex-end' },
                        flexWrap: 'wrap'
                      }}>
                        <Tooltip title="Editar miembro">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleEditClick(member)}
                            sx={{ 
                              borderColor: office.color,
                              color: office.color,
                              '&:hover': {
                                borderColor: office.color,
                                bgcolor: `${office.color}10`
                              }
                            }}
                          >
                            Editar
                          </Button>
                        </Tooltip>
                        {!isCurrentUser(member) && (
                          <Tooltip title="Eliminar miembro">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(member)}
                              sx={{ 
                                color: 'error.main',
                                '&:hover': { bgcolor: 'error.light', color: 'white' }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          display: 'block', 
                          mt: 1,
                          textAlign: { xs: 'left', md: 'right' }
                        }}
                      >
                        Desde {new Date(member.created_at).toLocaleDateString('es-ES', {
                          month: 'short',
                          year: 'numeric'
                        })}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Modal de edición mejorado */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6" fontWeight={600}>
            Editar Miembro
          </Typography>
          <IconButton onClick={() => setEditDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2.5}>
            <Grid container size={{xs:12}}>
              <Grid item size={{xs:12, sm:6}}>
                <TextField
                  fullWidth
                  label="Nombres"
                  value={editForm.first_names}
                  onChange={(e) =>
                    setEditForm({ ...editForm, first_names: e.target.value })
                  }
                  required
                />
              </Grid>

              <Grid item size={{xs:12, sm:6}}>
                <TextField
                  fullWidth
                  label="Apellidos"
                  value={editForm.last_names}
                  onChange={(e) =>
                    setEditForm({ ...editForm, last_names: e.target.value })
                  }
                  required
                />
              </Grid>
            </Grid>
            <Grid item size={{xs:12}}>
              <TextField
                fullWidth
                label="Correo electrónico"
                type="email"
                disabled
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item size={{xs:12, sm:6}}>
              <TextField
                fullWidth
                label="DNI"
                value={editForm.document_number}
                onChange={(e) => setEditForm({ ...editForm, document_number: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item size={{xs:12, sm:6}}>
              <TextField
                fullWidth
                label="Teléfono"
                value={editForm.phone_number}
                onChange={(e) => setEditForm({ ...editForm, phone_number: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item size={{xs:12}}>
              <FormControl fullWidth>
                <InputLabel>Rol en el despacho</InputLabel>
                <Select
                  value={editForm.role}
                  label="Rol en el despacho"
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                >
                  {Object.entries(ROLE_CONFIG).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Icon sx={{ color: config.color }} />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {config.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {config.description}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button 
            onClick={() => setEditDialogOpen(false)}
            disabled={savingChanges}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveChanges}
            disabled={savingChanges || !editForm.first_names || !editForm.last_names || !editForm.email}
            startIcon={savingChanges ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{ bgcolor: office.color }}
          >
            {savingChanges ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación de eliminación */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            Confirmar Eliminación
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar a <strong>{memberToDelete?.person?.full_name?.split(';').map(n => n.trim()).join(' ')}</strong> del despacho?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Esta acción no se puede deshacer. El miembro perderá acceso a todos los expedientes.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            startIcon={<DeleteIcon />}
          >
            Eliminar Miembro
          </Button>
        </DialogActions>
      </Dialog>

      <InviteMemberModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        officeId={office.id}
        officeColor={office.color}
        onMemberAdded={fetchMembers}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setSnackbarOpen(false)}
          sx={{ width: '100%' }}
        >
          Los permisos de los miembros se están sincronizando.  
          Puede tomar unos minutos en reflejarse los cambios.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OfficeMembersView;